import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { useSelector, useDispatch } from "react-redux";
import {
  Upload,
  Cpu,
  CheckCircle,
  Settings,
  Send,
  BarChart2,
  Zap,
  AlertCircle,
  X,
  Download,
  Crown,
} from "lucide-react";
import { analyzeImage } from "../../services/geminiService";
import { Footer, Navigation } from "../../components";
import { cn } from "../../lib/utils";
import {
  canPerformAnalysis,
  recordAnalysisComplete,
} from "../../services/analysisLimitService";
import { loginWithGoogle } from "../../app/features/auth";
import { getUserSubscription } from "../../services/subscriptionService";

// Transform API response to UI format
const transformAnalysisData = (apiData) => {
  if (!apiData || !apiData.personalityTraits) {
    return null;
  }

  // Extract numeric scores from text (simple heuristic)
  const extractScore = (text) => {
    if (!text) return 0;
    const match = text.match(/\d+/);
    return match ? parseInt(match[0]) : Math.floor(Math.random() * 30) + 70;
  };

  // Calculate overall score from attractiveness
  const overallScore = apiData.attractiveness?.score
    ? apiData.attractiveness.score
    : 7.5;

  // Get main trait from summary
  const mainTrait =
    apiData.personalityTraits?.summary?.split(".")[0] || "Strategic Leader";

  return {
    overallScore: overallScore,
    mainTrait: mainTrait,
    personalityTraits: [
      {
        name: "Confidence",
        score: extractScore(apiData.personalityTraits?.confidence),
      },
      {
        name: "Approachability",
        score: extractScore(apiData.personalityTraits?.approachability),
      },
      {
        name: "Professionalism",
        score: extractScore(apiData.personalityTraits?.professionalism),
      },
      {
        name: "Charisma",
        score: extractScore(apiData.personalityTraits?.charisma),
      },
    ],
    posture: [
      {
        name: "Alignment",
        score: extractScore(apiData.posture?.alignment),
      },
      {
        name: "Body Language",
        score: extractScore(apiData.posture?.bodyLanguage),
      },
      {
        name: "Gaze Direction",
        score: extractScore(apiData.posture?.gaze),
      },
      {
        name: "Hand Position",
        score: extractScore(apiData.posture?.hands),
      },
    ],
    attractiveness: apiData.attractiveness?.score || 7.5,
    suggestions: [
      ...(apiData.suggestions?.posture?.map((text) => ({
        category: "Objective",
        text: text,
        icon: Settings,
      })) || []),
      ...(apiData.suggestions?.appearance?.map((text) => ({
        category: "Appearance",
        text: text,
        icon: Zap,
      })) || []),
      ...(apiData.suggestions?.presentation?.map((text) => ({
        category: "Presentation",
        text: text,
        icon: Send,
      })) || []),
    ],
    rawData: apiData, // Keep original for reference
  };
};

// Step Indicator Component
const StepIndicator = ({ step, currentStep, icon: Icon, title }) => {
  const isActive = step === currentStep;
  const isCompleted = step < currentStep;

  let colorClass = "text-zinc-500";
  if (isCompleted) colorClass = "text-teal-400";
  if (isActive) colorClass = "text-rose-400";

  return (
    <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer">
      <div
        className={cn(
          "w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0",
          isActive
            ? "border-rose-400 bg-rose-500/20"
            : isCompleted
            ? "border-teal-400 bg-teal-500/20"
            : "border-stone-700 bg-stone-800"
        )}
      >
        <Icon className={cn("w-3.5 h-3.5 sm:w-4 sm:h-4", colorClass)} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={cn("text-xs font-semibold", colorClass)}
        >{`STEP ${step}`}</p>
        <p
          className={cn(
            "text-xs sm:text-sm truncate",
            isActive ? "text-white" : "text-zinc-300"
          )}
        >
          {title}
        </p>
      </div>
    </div>
  );
};

// Metric Bar Component
const MetricBar = ({ name, score, color }) => {
  const width = `${score}%`;
  return (
    <div className="mb-4">
      <div className="flex justify-between text-sm font-medium text-zinc-300">
        <span>{name}</span>
        <span>{score}</span>
      </div>
      <div className="w-full bg-stone-800 rounded-full h-2.5">
        <div
          className={cn("h-2.5 rounded-full", color)}
          style={{ width: width, transition: "width 0.5s ease-out" }}
        ></div>
      </div>
    </div>
  );
};

// Step 1: Photo Upload
const UploadStep = ({
  setStep,
  onFileSelect,
  file,
  setFile,
  session,
  user,
  onSignIn,
}) => {
  const [preview, setPreview] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const [timeUntilReset, setTimeUntilReset] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(false);

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);

      // Check limit when file is selected
      const checkLimit = async () => {
        const isSignedIn = !!session && !!user;
        if (!isSignedIn) {
          setCheckingLimit(true);
          try {
            const limitCheck = await canPerformAnalysis(isSignedIn);
            if (!limitCheck.canAnalyze) {
              setLimitReached(true);
              setTimeUntilReset(limitCheck.timeUntilReset || null);
            } else {
              setLimitReached(false);
              setTimeUntilReset(null);
            }
          } catch (err) {
            console.error("Error checking limit:", err);
            // Fail open - allow if check fails
            setLimitReached(false);
          } finally {
            setCheckingLimit(false);
          }
        } else {
          setLimitReached(false);
        }
      };

      checkLimit();
    } else {
      setPreview("");
      setLimitReached(false);
      setTimeUntilReset(null);
    }
  }, [file, session, user]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const picked = acceptedFiles?.[0];
      if (picked) {
        setFile(picked);
        onFileSelect(picked);
      }
    },
    [setFile, onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
  });

  const handleRemove = () => {
    setFile(null);
    setPreview("");
    onFileSelect(null);
  };

  const fileSelected = !!file;

  return (
    <div className="flex flex-col items-center p-6 sm:p-10">
      <h2 className="text-3xl font-bold text-white mb-2">
        1. Prepare Your Photo
      </h2>
      <p className="text-zinc-400 mb-8 text-center max-w-lg">
        Upload a clear headshot or full-body photo for the most accurate AI
        analysis.
      </p>

      {/* Drop Zone - Only show when no file is selected */}
      {!fileSelected && (
        <div
          {...getRootProps()}
          className={cn(
            "w-full max-w-xl p-12 border-4 border-dashed rounded-xl transition duration-300 cursor-pointer",
            isDragActive
              ? "border-rose-500 bg-rose-500/10"
              : "border-stone-700 bg-stone-800/50 hover:border-stone-600"
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center justify-center">
            <Upload className="w-10 h-10 text-zinc-500" />
            <p className="mt-4 text-lg font-semibold text-white">
              Drag & Drop or Click to Upload
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              JPEG, PNG, or WEBP (Max. 10MB)
            </p>
          </div>
        </div>
      )}

      {/* Image Preview - Only show when file is selected */}
      {fileSelected && preview && (
        <div className="w-full max-w-xl relative group">
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-auto max-h-96 object-contain rounded-xl border-2 border-teal-500/50 bg-stone-800/50"
            />
            {/* Close Button */}
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all shadow-lg hover:scale-110 z-10"
              aria-label="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 text-center">
            <p className="text-sm text-teal-400 font-semibold">
              Photo Ready for Analysis!
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              {file?.name} ({(file?.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          </div>
        </div>
      )}

      {/* Limit Reached Alert */}
      {limitReached && fileSelected && (
        <div className="mt-6 w-full max-w-xl p-6 bg-stone-900/90 border-2 border-rose-500/50 rounded-xl">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 mb-4 rounded-full flex items-center justify-center bg-rose-600">
              <AlertCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Daily Limit Reached
            </h3>
            <p className="text-zinc-400 mb-4 text-sm">
              You've used your free daily analysis. Sign in with Google to get
              unlimited analyses!
            </p>
            {timeUntilReset && (
              <p className="text-xs text-zinc-500 mb-4">
                Your limit will reset in{" "}
                {Math.floor(timeUntilReset / 60) > 0
                  ? `${Math.floor(timeUntilReset / 60)} hour${
                      Math.floor(timeUntilReset / 60) > 1 ? "s" : ""
                    } and `
                  : ""}
                {timeUntilReset % 60} minute
                {timeUntilReset % 60 !== 1 ? "s" : ""}
              </p>
            )}
            <button
              onClick={onSignIn}
              className="px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-teal-600 to-rose-600 hover:from-teal-700 hover:to-rose-700 text-white transition duration-300 shadow-xl flex items-center gap-2 mb-3"
            >
              <Zap className="w-4 h-4" />
              Sign in with Google for Unlimited Analyses
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => {
          if (!limitReached && fileSelected) {
            setStep(2);
          }
        }}
        disabled={!fileSelected || limitReached || checkingLimit}
        className={cn(
          "mt-10 px-8 py-3 rounded-lg font-semibold text-lg transition duration-300 shadow-xl",
          fileSelected && !limitReached
            ? "bg-gradient-to-r from-rose-600 to-teal-600 hover:from-rose-700 hover:to-teal-700 text-white"
            : "bg-stone-700 text-zinc-500 cursor-not-allowed"
        )}
      >
        {checkingLimit
          ? "Checking..."
          : fileSelected && !limitReached
          ? "Start AI Analysis"
          : "Awaiting Photo"}
      </button>

      <div className="mt-12 w-full max-w-xl">
        <h3 className="text-xl font-semibold text-white mb-4 border-b border-stone-700 pb-2">
          Tips for Best Results
        </h3>
        <ul className="text-zinc-400 list-disc list-inside space-y-2 text-sm">
          <li>Use photos taken in good, consistent lighting.</li>
          <li>Ensure your face and body are fully visible.</li>
          <li>A neutral background often yields cleaner results.</li>
        </ul>
      </div>
    </div>
  );
};

// Step 2: Processing
const ProcessingStep = ({
  setStep,
  onAnalysisComplete,
  isLoading,
  file,
  limitReached,
  timeUntilReset,
  onSignIn,
  subscription,
  usageInfo,
  limitCheck,
}) => {
  const navigate = useNavigate();
  const [animationProgress, setAnimationProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isFinished, setIsFinished] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [preview, setPreview] = useState("");

  // Show image preview if file is provided
  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  }, [file]);

  const magicalMessages = [
    "✨ Unlocking the secrets of your presence...",
    "🌟 Reading the energy of your image...",
    "💫 Discovering your unique charisma...",
    "✨ Mapping your personality constellation...",
    "🌟 Revealing your professional aura...",
    "💫 Crafting your personalized insights...",
    "✨ Almost there! Finalizing your magical report...",
  ];

  const [apiStartTime, setApiStartTime] = useState(null);
  const [apiProgressTick, setApiProgressTick] = useState(0);

  // Calculate total progress: 0-70% for animation, 70-100% for API
  // apiProgressTick is used to trigger recalculation during API loading
  const totalProgress = (() => {
    if (!animationComplete) {
      // Animation phase: 0-70%
      return Math.min((animationProgress / 100) * 70, 70);
    } else if (isLoading && apiStartTime) {
      // API loading phase: 70-95% (smoothly progress during API call)
      // apiProgressTick ensures this recalculates on each interval tick
      const elapsed = Date.now() - apiStartTime;
      const apiProgressPercent = Math.min(elapsed / 20000, 0.95); // 20 seconds to reach 95%
      // Reference apiProgressTick to trigger recalculation
      return 70 + apiProgressPercent * 25 + apiProgressTick * 0; // Use tick to trigger updates
    } else if (!isLoading && animationComplete) {
      // API complete: 100%
      return 100;
    } else {
      // Waiting for API to start
      return 70;
    }
  })();

  useEffect(() => {
    let timeout;
    let index = 0;
    const totalSteps = magicalMessages.length;
    const progressIncrement = 100 / totalSteps;

    const processStep = () => {
      if (index < totalSteps) {
        setCurrentMessage(magicalMessages[index]);
        setAnimationProgress((index + 1) * progressIncrement);
        index++;
        timeout = setTimeout(processStep, 1200 + Math.random() * 400);
      } else {
        // Animation done, start API call
        setAnimationProgress(100);
        setCurrentMessage("✨ Connecting to AI models...");
        setAnimationComplete(true);
        onAnalysisComplete();
      }
    };

    processStep();

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAnalysisComplete]);

  // Update message and track API start time when API loading state changes
  useEffect(() => {
    if (animationComplete) {
      if (isLoading && !apiStartTime) {
        // API call just started
        setApiStartTime(Date.now());
        setCurrentMessage("✨ Processing with AI models...");
      } else if (!isLoading && apiStartTime) {
        // API call completed
        setCurrentMessage("✨ Analysis complete!");
        setIsFinished(true);
      }
    }
  }, [isLoading, animationComplete, apiStartTime]);

  // Smooth progress animation during API call
  useEffect(() => {
    if (isLoading && apiStartTime) {
      const interval = setInterval(() => {
        // Update tick to trigger progress recalculation
        setApiProgressTick((prev) => prev + 1);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isLoading, apiStartTime]);

  // Show limit reached message if limit is reached
  if (limitReached) {
    const hours = timeUntilReset ? Math.floor(timeUntilReset / 60) : 0;
    const minutes = timeUntilReset ? timeUntilReset % 60 : 0;
    const planType = subscription?.plan_type || "free";
    const isFree = planType === "free";
    const isPlanB = planType === "plan_b";
    const remaining = limitCheck?.remaining || 0;

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-6 sm:p-10">
        <div className="w-20 h-20 mb-8 rounded-full flex items-center justify-center bg-rose-600">
          <AlertCircle className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-4 text-center">
          {isFree ? "Daily Limit Reached" : "Usage Limit Reached"}
        </h2>
        <p className="text-zinc-400 mb-6 max-w-md text-center">
          {isFree
            ? "You've used your free daily analysis. Subscribe to a plan for more analyses!"
            : isPlanB
            ? `You've used all 5 of your daily analyses. Upgrade to Pro for unlimited analyses!`
            : "Your analysis limit has been reached."}
        </p>
        {timeUntilReset && (
          <p className="text-sm text-zinc-500 mb-4 text-center">
            Your limit will reset in{" "}
            {hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""} and ` : ""}
            {minutes} minute{minutes !== 1 ? "s" : ""}
          </p>
        )}
        {isFree && (
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button
              onClick={() => navigate("/pricing")}
              className="px-8 py-3 rounded-lg font-semibold text-lg bg-gradient-to-r from-teal-600 to-rose-600 hover:from-teal-700 hover:to-rose-700 text-white transition duration-300 shadow-xl flex items-center justify-center gap-2"
            >
              <Crown className="w-5 h-5" />
              View Plans
            </button>
            <button
              onClick={onSignIn}
              className="px-8 py-3 rounded-lg font-semibold text-lg bg-stone-700 hover:bg-stone-600 text-white transition duration-300 shadow-xl flex items-center justify-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Sign In
            </button>
          </div>
        )}
        {isPlanB && (
          <button
            onClick={() => navigate("/subscription")}
            className="px-8 py-3 rounded-lg font-semibold text-lg bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white transition duration-300 shadow-xl flex items-center justify-center gap-2 mb-4"
          >
            <Crown className="w-5 h-5" />
            Upgrade to Unlimited
          </button>
        )}
        <button
          onClick={() => setStep(1)}
          className="text-zinc-400 hover:text-white text-sm transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-6 sm:p-10">
      {/* Image Preview */}
      {preview && (
        <div className="w-full max-w-md mb-8">
          <div className="relative">
            <img
              src={preview}
              alt="Processing"
              className="w-full h-auto max-h-64 object-contain rounded-xl border-2 border-teal-500/50 bg-stone-800/50"
            />
            <div className="absolute inset-0 bg-stone-900/30 rounded-xl flex items-center justify-center">
              <div className="text-center">
                <Cpu className="w-12 h-12 text-teal-400 animate-pulse mx-auto mb-2" />
                <p className="text-sm text-teal-400 font-semibold">
                  Processing...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={cn(
          "w-20 h-20 mb-8 rounded-full flex items-center justify-center",
          isFinished ? "bg-teal-600" : "bg-rose-600"
        )}
      >
        {isFinished ? (
          <CheckCircle className="w-10 h-10 text-white" />
        ) : (
          <Cpu className="w-10 h-10 text-white animate-pulse" />
        )}
      </div>
      <h2 className="text-3xl font-bold text-white mb-4">
        {isFinished ? "Analysis Complete!" : "AI Analysis in Progress..."}
      </h2>
      <p className="text-zinc-400 mb-10 max-w-md text-center">
        {isFinished
          ? "Your comprehensive personality dashboard is ready."
          : "Hold tight while we weave magic into your analysis."}
      </p>

      {/* Progress Bar */}
      <div className="w-full max-w-xl mb-6">
        <div className="w-full bg-stone-800 rounded-full h-4 mb-4 overflow-hidden">
          <div
            className="h-4 rounded-full bg-gradient-to-r from-rose-500 via-pink-500 to-teal-500 transition-all duration-500 ease-out"
            style={{ width: `${totalProgress}%` }}
          ></div>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-white mb-2">
            {Math.round(totalProgress)}%
          </p>
          <p className="text-sm text-rose-400 font-medium animate-pulse">
            {currentMessage}
          </p>
        </div>
      </div>

      {isFinished && !isLoading && (
        <button
          onClick={() => setStep(3)}
          className="mt-10 px-12 py-3 rounded-lg font-semibold text-lg bg-gradient-to-r from-teal-600 to-rose-600 hover:from-teal-700 hover:to-rose-700 text-white transition duration-300 shadow-xl"
        >
          View Full Dashboard
        </button>
      )}
    </div>
  );
};

// Step 3: Results Dashboard
const ResultsStep = ({ results, isLoading, file }) => {
  const [activeCategory, setActiveCategory] = useState("Objective");
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  }, [file]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-6 sm:p-10">
        <div className="w-20 h-20 mb-8 rounded-full flex items-center justify-center bg-rose-600">
          <Cpu className="w-10 h-10 text-white animate-pulse" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-4">
          AI Analysis in Progress...
        </h2>
        <p className="text-zinc-400 mb-10 max-w-md text-center">
          Processing your image with AI models. This may take a moment...
        </p>
        <div className="w-full max-w-xl bg-stone-800 p-6 rounded-lg shadow-xl">
          <div className="flex items-center space-x-3">
            <Cpu className="w-5 h-5 text-rose-400 animate-spin" />
            <span className="text-sm text-white">
              Analyzing image and generating insights...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="p-8 text-center">
        <p className="text-zinc-400">No results available.</p>
      </div>
    );
  }

  // Calculate radar point coordinates
  const renderRadarPoint = (score, index, total) => {
    const radius = 60;
    const angle = (2 * Math.PI * index) / total - Math.PI / 2;
    const r = (score / 100) * radius;
    const x = r * Math.cos(angle) + radius + 10;
    const y = r * Math.sin(angle) + radius + 10;
    return `${x},${y}`;
  };

  const personalityPoints = results.personalityTraits
    .map((t, i) =>
      renderRadarPoint(t.score, i, results.personalityTraits.length)
    )
    .join(" ");

  const getAttractivenessColor = (score) => {
    if (score > 8.0) return "text-teal-400";
    if (score > 6.0) return "text-yellow-400";
    return "text-rose-400";
  };

  const filteredSuggestions = results.suggestions.filter(
    (s) => s.category === activeCategory
  );

  // Get all suggestions for print (not filtered)
  const allSuggestions = results.suggestions || [];

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 1.5cm;
          }
          
          body * {
            visibility: hidden;
          }
          
          .printable-content {
            display: block !important;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            color: black !important;
            padding: 0;
            visibility: visible;
          }
          
          .printable-content * {
            visibility: visible;
            color: black !important;
          }
          
          .no-print {
            display: none !important;
            visibility: hidden !important;
          }
          
          nav,
          header,
          button,
          .no-print {
            display: none !important;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
          }
          
          .print-section {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          
          .print-image {
            max-width: 100%;
            height: auto;
            margin: 15px 0;
            border: 1px solid #ddd;
          }
          
          .print-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin: 20px 0;
          }
          
          .print-metric {
            border: 1px solid #ddd;
            padding: 10px;
            border-radius: 5px;
          }
          
          .print-suggestions {
            margin-top: 20px;
          }
          
          .print-suggestion-item {
            margin-bottom: 10px;
            padding: 10px;
            border-left: 3px solid #e11d48;
            background: #f9fafb;
          }
        }
      `}</style>

      {/* Printable Content - Hidden on screen, shown when printing */}
      <div
        className="printable-content"
        style={{ display: "none", position: "absolute", left: "-9999px" }}
      >
        <div className="print-header">
          <h1 style={{ fontSize: "28px", margin: "0 0 10px 0", color: "#000" }}>
            FaceTraits - Personality Analysis Report
          </h1>
          <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
            Generated on {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Analyzed Image */}
        {imagePreview && (
          <div className="print-section">
            <h2
              style={{ fontSize: "20px", marginBottom: "10px", color: "#000" }}
            >
              Analyzed Image
            </h2>
            <img
              src={imagePreview}
              alt="Analyzed"
              className="print-image"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </div>
        )}

        {/* Overall Persona */}
        <div className="print-section">
          <h2 style={{ fontSize: "20px", marginBottom: "10px", color: "#000" }}>
            Overall Persona
          </h2>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "15px",
              border: "1px solid #ddd",
              borderRadius: "5px",
            }}
          >
            <div>
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  margin: "0 0 5px 0",
                  textTransform: "uppercase",
                }}
              >
                Overall Persona
              </p>
              <h3 style={{ fontSize: "24px", margin: 0, color: "#000" }}>
                {results.mainTrait}
              </h3>
            </div>
            <div style={{ textAlign: "right" }}>
              <p
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  margin: 0,
                  color: "#000",
                }}
              >
                {results.overallScore.toFixed(1)}
              </p>
              <p
                style={{ fontSize: "12px", color: "#666", margin: "5px 0 0 0" }}
              >
                Composite Score / 10.0
              </p>
            </div>
          </div>
        </div>

        {/* Detailed Metrics */}
        <div className="print-section">
          <h2 style={{ fontSize: "20px", marginBottom: "15px", color: "#000" }}>
            Detailed Metrics
          </h2>
          <div className="print-grid">
            {/* Personality Traits */}
            <div className="print-metric">
              <h3
                style={{
                  fontSize: "16px",
                  marginBottom: "10px",
                  color: "#000",
                }}
              >
                Personality Traits
              </h3>
              {results.personalityTraits.map((trait) => (
                <div key={trait.name} style={{ marginBottom: "8px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "3px",
                    }}
                  >
                    <span style={{ fontSize: "12px", color: "#000" }}>
                      {trait.name}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#000",
                      }}
                    >
                      {trait.score}
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "6px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${trait.score}%`,
                        height: "100%",
                        backgroundColor: "#e11d48",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Posture & Body Language */}
            <div className="print-metric">
              <h3
                style={{
                  fontSize: "16px",
                  marginBottom: "10px",
                  color: "#000",
                }}
              >
                Posture & Body Language
              </h3>
              {results.posture.map((p) => (
                <div key={p.name} style={{ marginBottom: "8px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "3px",
                    }}
                  >
                    <span style={{ fontSize: "12px", color: "#000" }}>
                      {p.name}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#000",
                      }}
                    >
                      {p.score}
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "6px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${p.score}%`,
                        height: "100%",
                        backgroundColor: "#14b8a6",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Key Visual Metrics */}
            <div className="print-metric">
              <h3
                style={{
                  fontSize: "16px",
                  marginBottom: "10px",
                  color: "#000",
                }}
              >
                Key Visual Metrics
              </h3>
              <div style={{ marginBottom: "15px" }}>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    margin: "0 0 5px 0",
                    textTransform: "uppercase",
                  }}
                >
                  Attractiveness Score
                </p>
                <p
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    margin: 0,
                    color: "#000",
                  }}
                >
                  {results.attractiveness.toFixed(1)}{" "}
                  <span style={{ fontSize: "16px", color: "#666" }}>/ 10</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Improvement Suggestions */}
        <div className="print-section print-suggestions">
          <h2 style={{ fontSize: "20px", marginBottom: "15px", color: "#000" }}>
            Improvement Suggestions
          </h2>
          {["Objective", "Appearance", "Presentation"].map((category) => {
            const categorySuggestions = allSuggestions.filter(
              (s) => s.category === category
            );
            if (categorySuggestions.length === 0) return null;

            return (
              <div key={category} style={{ marginBottom: "20px" }}>
                <h3
                  style={{
                    fontSize: "16px",
                    marginBottom: "10px",
                    color: "#000",
                    borderBottom: "1px solid #ddd",
                    paddingBottom: "5px",
                  }}
                >
                  {category}
                </h3>
                {categorySuggestions.map((suggestion, index) => (
                  <div key={index} className="print-suggestion-item">
                    <p style={{ fontSize: "14px", margin: 0, color: "#000" }}>
                      {index + 1}. {suggestion.text}
                    </p>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: "30px",
            paddingTop: "15px",
            borderTop: "1px solid #ddd",
            textAlign: "center",
            fontSize: "12px",
            color: "#666",
          }}
        >
          <p style={{ margin: 0 }}>
            This report was generated by FaceTraits using advanced AI analysis.
          </p>
          <p style={{ margin: "5px 0 0 0" }}>
            Scores are relative and calculated using thousands of benchmark
            images.
          </p>
        </div>
      </div>

      {/* Regular Screen Content */}
      <div className="p-3 sm:p-4 md:p-6 lg:p-8 no-print">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
          3. Insights Dashboard
        </h2>
        <p className="text-rose-400 text-base sm:text-lg mb-6 sm:mb-8">
          Your AI-driven analysis is complete.
        </p>

        {/* Processed Image Display */}
        {imagePreview && (
          <div className="mb-6 sm:mb-8 bg-stone-800 p-4 sm:p-6 rounded-xl shadow-2xl border border-stone-700">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4 flex items-center">
              <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-teal-400 mr-2" />
              Analyzed Image
            </h3>
            <div className="flex justify-center">
              <img
                src={imagePreview}
                alt="Analyzed"
                className="max-w-full h-auto max-h-64 sm:max-h-80 md:max-h-96 object-contain rounded-lg border-2 border-teal-500/50 bg-stone-900/50"
              />
            </div>
          </div>
        )}

        {/* Improvement Suggestions */}
        <div className="bg-gradient-to-br from-stone-900 via-stone-900 to-stone-800 p-4 sm:p-6 md:p-8 rounded-2xl shadow-2xl border border-stone-700 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
            <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center mb-2 sm:mb-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-rose-500 to-teal-500 flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
              </div>
              <span className="break-words">Improvement Suggestions</span>
            </h4>
            <div className="hidden md:flex items-center space-x-2 text-sm text-zinc-400">
              <span className="w-2 h-2 rounded-full bg-teal-400"></span>
              <span>AI-Powered Insights</span>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-2 mb-6 sm:mb-8 bg-stone-800/50 p-1.5 rounded-xl border border-stone-700">
            {["Objective", "Appearance", "Presentation"].map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={cn(
                  "flex-1 min-w-0 px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-300 whitespace-nowrap overflow-hidden text-ellipsis",
                  activeCategory === category
                    ? "bg-gradient-to-r from-rose-600 to-teal-600 text-white shadow-lg sm:transform sm:scale-105"
                    : "text-zinc-400 hover:text-white hover:bg-stone-700/50"
                )}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Suggestions List */}
          <div className="space-y-3 sm:space-y-4">
            {filteredSuggestions.length > 0 ? (
              filteredSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="group bg-stone-800/80 hover:bg-stone-800 border border-stone-700 hover:border-rose-500/50 p-4 sm:p-5 md:p-6 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-rose-500/10 flex flex-col sm:flex-row items-start gap-3 sm:gap-4"
                >
                  {/* Number Badge and Icon Container */}
                  <div className="flex items-center gap-3 sm:flex-col sm:items-start">
                    {/* Number Badge */}
                    <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-rose-500/20 to-teal-500/20 border border-rose-500/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-xs sm:text-sm font-bold text-rose-400">
                        {index + 1}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-rose-500/20 to-teal-500/20 flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-rose-500/30 group-hover:to-teal-500/30 transition-colors duration-300">
                        <suggestion.icon className="w-4 h-4 sm:w-5 sm:h-5 text-rose-400" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-zinc-100 text-sm sm:text-base leading-relaxed font-medium group-hover:text-white transition-colors duration-300 break-words">
                      {suggestion.text}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider whitespace-nowrap">
                        {suggestion.category}
                      </span>
                      <span className="text-zinc-600 hidden sm:inline">•</span>
                      <span className="text-xs text-teal-400 font-medium whitespace-nowrap">
                        Actionable Tip
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-stone-800/50 border border-stone-700 rounded-xl p-8 sm:p-12 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-stone-700/50 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-zinc-500" />
                </div>
                <p className="text-zinc-400 text-base sm:text-lg font-medium">
                  No suggestions available for this category.
                </p>
                <p className="text-zinc-500 text-xs sm:text-sm mt-2">
                  Try selecting a different category above.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Main Summary Card */}
        <div className="bg-stone-800 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8 border-l-4 border-rose-500 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm text-zinc-400 uppercase tracking-wider">
              Overall Persona
            </p>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mt-1 break-words">
              {results.mainTrait}
            </h3>
          </div>
          <div className="mt-2 md:mt-0 text-center md:text-right flex-shrink-0">
            <p className="text-4xl sm:text-5xl md:text-6xl font-black bg-gradient-to-r from-rose-400 to-teal-400 bg-clip-text text-transparent">
              {results.overallScore.toFixed(1)}
            </p>
            <p className="text-xs sm:text-sm text-zinc-400">
              Composite Score / 10.0
            </p>
          </div>
        </div>

        {/* Detailed Metrics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-10">
          {/* Column 1: Personality Traits */}
          <div className="bg-stone-800 p-4 sm:p-6 rounded-xl shadow-lg">
            <h4 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-5 flex items-center">
              <BarChart2 className="w-4 h-4 sm:w-5 sm:h-5 text-rose-400 mr-2 flex-shrink-0" />
              <span className="break-words">Personality Traits</span>
            </h4>
            {results.personalityTraits.map((trait) => (
              <MetricBar
                key={trait.name}
                name={trait.name}
                score={trait.score}
                color="bg-rose-500"
              />
            ))}
          </div>

          {/* Column 2: Posture & Body Language */}
          <div className="bg-stone-800 p-4 sm:p-6 rounded-xl shadow-lg">
            <h4 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-5 flex items-center">
              <BarChart2 className="w-4 h-4 sm:w-5 sm:h-5 text-teal-400 mr-2 flex-shrink-0" />
              <span className="break-words">Posture & Body Language</span>
            </h4>
            {results.posture.map((p) => (
              <MetricBar
                key={p.name}
                name={p.name}
                score={p.score}
                color="bg-teal-500"
              />
            ))}
          </div>

          {/* Column 3: Attractiveness Gauge & Radar Chart */}
          <div className="bg-stone-800 p-4 sm:p-6 rounded-xl shadow-lg flex flex-col justify-between">
            <h4 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-5 flex items-center">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 mr-2 flex-shrink-0" />
              <span className="break-words">Key Visual Metrics</span>
            </h4>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
              <div>
                <p className="text-xs sm:text-sm text-zinc-400 uppercase">
                  Attractiveness Score
                </p>
                <p
                  className={cn(
                    "text-3xl sm:text-4xl font-extrabold",
                    getAttractivenessColor(results.attractiveness)
                  )}
                >
                  {results.attractiveness.toFixed(1)}{" "}
                  <span className="text-zinc-500 text-lg sm:text-xl">/ 10</span>
                </p>
              </div>
              {/* Mock Radar Chart */}
              <svg
                width="100"
                height="100"
                viewBox="0 0 140 140"
                className="opacity-75 flex-shrink-0 sm:w-[140px] sm:h-[140px]"
              >
                <g transform="translate(10, 10)">
                  <polygon
                    points="70,10 130,50 110,120 30,120 10,50"
                    stroke="#52525b"
                    fill="none"
                    strokeWidth="1"
                  />
                  <polygon
                    points="70,40 115,65 100,105 40,105 25,65"
                    stroke="#52525b"
                    fill="none"
                    strokeWidth="1"
                  />
                  <polygon
                    points={personalityPoints}
                    fill="rgba(251, 113, 133, 0.4)"
                    stroke="#fb7185"
                    strokeWidth="2"
                  />
                </g>
                <text
                  x="60"
                  y="130"
                  className="text-xs text-zinc-400 font-medium fill-zinc-400"
                >
                  Overall Visualization
                </text>
              </svg>
            </div>
            <p className="text-xs sm:text-sm text-zinc-500 mt-auto">
              Scores are relative and calculated using thousands of benchmark
              images.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

// Main Component
const PersonalityAnalysis = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session, user } = useSelector((state) => state.auth);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [timeUntilReset, setTimeUntilReset] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usageInfo, setUsageInfo] = useState(null);
  const analysisInProgressRef = React.useRef(false);
  const limitCheckDataRef = React.useRef(null);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, []);

  // Check if image was passed from home page navigation
  useEffect(() => {
    if (location.state?.imageFile) {
      const imageFile = location.state.imageFile;
      setFile(imageFile);
      // Jump to step 2 (image generation/processing) when image is provided
      setStep(2);
    }
  }, [location.state]);

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setError("");
  };

  const handleAnalysisComplete = React.useCallback(async () => {
    if (!file || analysisInProgressRef.current) return;

    // Check if user is signed in
    const isSignedIn = !!session && !!user;
    const userId = user?.id || null;

    // Check analysis limit for both signed-in and anonymous users
    setIsLoading(true);
    setError("");
    setLimitReached(false);

    try {
      const limitCheck = await canPerformAnalysis(isSignedIn, userId);
      limitCheckDataRef.current = limitCheck;

      // Store subscription and usage info for display
      if (limitCheck.subscription) {
        setSubscription(limitCheck.subscription);
      }
      if (limitCheck.usage) {
        setUsageInfo(limitCheck.usage);
      }

      if (!limitCheck.canAnalyze) {
        setLimitReached(true);
        setTimeUntilReset(limitCheck.timeUntilReset || null);
        setIsLoading(false);
        analysisInProgressRef.current = false;
        return;
      }
    } catch (err) {
      console.error("Error checking analysis limit:", err);
      // Fail open - allow analysis if limit check fails
    }

    // Prevent double execution
    analysisInProgressRef.current = true;
    setError("");
    setIsLoading(true);
    setLimitReached(false);

    try {
      const result = await analyzeImage(file);
      const transformed = transformAnalysisData(result);
      setAnalysis(transformed);

      // Record analysis completion
      if (limitCheckDataRef.current) {
        const checkData = limitCheckDataRef.current;
        await recordAnalysisComplete(
          isSignedIn,
          userId,
          checkData.ipAddress,
          checkData.fingerprint,
          checkData.anonymousId
        );
      }

      // Automatically move to step 3 when analysis completes
      setStep(3);
    } catch (err) {
      setError(err?.message || "Analysis failed. Please try again.");
    } finally {
      setIsLoading(false);
      analysisInProgressRef.current = false;
    }
  }, [file, session, user]);

  const handleSetStep = useCallback((newStep) => {
    if (newStep === 2) {
      setStep(2);
      // ProcessingStep will call handleAnalysisComplete when done
    } else {
      setStep(newStep);
    }
  }, []);

  const handleAnalyzeNew = () => {
    setStep(1);
    setFile(null);
    setAnalysis(null);
    setError("");
    setIsLoading(false);
  };

  const handleDownloadPDF = () => {
    // Trigger print dialog - print styles will handle showing/hiding content
    window.print();
  };

  const handleSignIn = async () => {
    try {
      await dispatch(loginWithGoogle()).unwrap();
    } catch (error) {
      console.error("Error signing in:", error);
      setError("Failed to sign in. Please try again.");
    }
  };

  const headerTitle =
    step === 1
      ? "Upload Photo"
      : step === 2
      ? "AI Processing"
      : "Review Insights";

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <UploadStep
            setStep={handleSetStep}
            onFileSelect={handleFileSelect}
            file={file}
            setFile={setFile}
            session={session}
            user={user}
            onSignIn={handleSignIn}
          />
        );
      case 2:
        return (
          <ProcessingStep
            setStep={setStep}
            onAnalysisComplete={handleAnalysisComplete}
            isLoading={isLoading}
            file={file}
            limitReached={limitReached}
            timeUntilReset={timeUntilReset}
            onSignIn={handleSignIn}
            subscription={subscription}
            usageInfo={usageInfo}
            limitCheck={limitCheckDataRef.current}
          />
        );
      case 3:
        return (
          <ResultsStep results={analysis} isLoading={isLoading} file={file} />
        );
      default:
        return (
          <UploadStep
            setStep={handleSetStep}
            onFileSelect={handleFileSelect}
            file={file}
            setFile={setFile}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans selection:bg-teal-500/30">
      <Navigation />

      <div className="pt-20 sm:pt-24 pb-8 sm:pb-12 px-3 sm:px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="w-full bg-stone-900 rounded-xl shadow-2xl border border-stone-800">
            <main className="flex flex-col lg:flex-row space-y-4 sm:space-y-6 lg:space-y-0 lg:space-x-6 xl:space-x-8 p-3 sm:p-4 md:p-6">
              {/* Left Sidebar: Step Navigation */}
              <div className="w-full lg:w-64 p-4 sm:p-6 bg-stone-800 rounded-xl shadow-lg h-fit border border-stone-700">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 border-b border-stone-700 pb-2 sm:pb-3">
                  Analysis Flow
                </h2>
                <div className="space-y-6">
                  <StepIndicator
                    step={1}
                    currentStep={step}
                    icon={Upload}
                    title="Photo Upload"
                  />
                  <div
                    className={cn(
                      "h-8 w-px ml-4",
                      step > 1 ? "bg-rose-500" : "bg-stone-700"
                    )}
                  ></div>
                  <StepIndicator
                    step={2}
                    currentStep={step}
                    icon={Cpu}
                    title="AI Processing"
                  />
                  <div
                    className={cn(
                      "h-8 w-px ml-4",
                      step > 2 ? "bg-rose-500" : "bg-stone-700"
                    )}
                  ></div>
                  <StepIndicator
                    step={3}
                    currentStep={step}
                    icon={CheckCircle}
                    title="Final Dashboard"
                  />
                </div>
              </div>

              {/* Right Content Area */}
              <div className="flex-1 bg-stone-800 rounded-xl shadow-lg min-h-[400px] sm:min-h-[600px] md:min-h-[700px] border border-stone-700">
                <div className="p-3 sm:p-4 md:p-6 border-b border-stone-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                  <h1 className="text-xl sm:text-2xl font-bold text-white break-words">
                    {headerTitle}
                  </h1>
                  {step === 3 && (
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      <button
                        onClick={handleDownloadPDF}
                        className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-sm sm:text-base bg-gradient-to-r from-teal-600 to-rose-600 hover:from-teal-700 hover:to-rose-700 text-white transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2 whitespace-nowrap"
                      >
                        <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Download PDF</span>
                      </button>
                      <button
                        onClick={handleAnalyzeNew}
                        className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-sm sm:text-base bg-gradient-to-r from-rose-600 to-teal-600 hover:from-rose-700 hover:to-teal-700 text-white transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2 whitespace-nowrap"
                      >
                        <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Analyze New</span>
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-1 sm:p-2">
                  {error && (
                    <div className="mb-4 p-4 rounded-xl border border-red-500/50 bg-red-500/10 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-red-400 font-medium">
                          {error}
                        </p>
                      </div>
                      <button
                        onClick={() => setError("")}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {error && (
                    <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}
                  {renderStepContent()}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PersonalityAnalysis;
